import { HttpMethod } from "@/lib/utils/http-methods";
import { Tooltip } from "@bryntum/core-thin";
import type { ResourceModel } from "@bryntum/scheduler-thin";
import { BryntumSchedulerPro } from "@bryntum/schedulerpro-react-thin";
import type { EventModel, SchedulerPro } from "@bryntum/schedulerpro-thin";
// Bryntum v7 CSS
import "../bryntum-common.css";
import "@bryntum/schedulerpro-thin/schedulerpro.css";
import "@bryntum/schedulerpro-thin/stockholm-light.css";
import type { SchedulerCalendar } from "@laborchart-modules/lc-core-api/dist/api/assignments/load-resource";
import { AuthAction, usePermissionContext } from "@/react/providers/permission-context-provider";
import { CaretsInVertical, CaretsOutVertical, Excavator } from "@procore/core-icons";
import { Flex, Link, P, Typography, useI18nContext } from "@procore/core-react";
import type { FC, RefObject } from "react";
import React, { useEffect, useMemo, useRef } from "react";
import { renderToString } from "react-dom/server";
import type { GanttTearsheetRef } from "../gantt-tearsheets";
import { GanttTooltipComponent } from "../gantt-tooltip-component";
import {
   buildCommonConfigurePanelItems,
   buildConfigurePanelStrip,
} from "../gantt-config-panel-strips";
import {
   getColorShades,
   getTextColorForBackground,
   getTimeOffReasonDisplay,
   getTimeRangesFeature,
} from "../gantt-utils";

import { renderTimeOffTooltip } from "../shared/components/time-off-tooltip";
import type {
   EventTooltipProps,
   HeaderRenderData,
   ResourceScheduleProps,
   SchedulerResource,
} from "./resource-schedule-types";
import {
   DragResizeRecordKey,
   EventLayout,
   loadUrl,
   SCHEDULER_EVENT_LAYOUT_KEY,
   SchedulerElementClass,
   syncUrl,
   TooltipElementType,
} from "./resource-schedule-types";
import { ResourceType } from "@/react/prop-types";
import {
   getGanttConfigurePanelValues,
   updateGanttConfigurePanelLocalStorage,
} from "../gantt-config-panel";
import type { AssignmentBarColors } from "../prop-types";
import {
   adjustEventEndDate,
   createTransformPayloadBeforeSend,
   extractTimeOffTooltipData,
   getBeforeDragCreateFinalizeHandler,
   getDragResizeFeatureConfig,
   getEventDragCreateFeatureConfig,
   normalizeDateTimeFields,
   prepareEventTooltipProps,
   validateEventPlacement,
} from "./resource-schedule-utils";
import {
   handleEditAssignment,
   handleUnassignAssignment,
   handleDeleteAssignment,
   handleDragCreateAssignment,
} from "./resource-schedule-menu-handlers";
import type { DragCreateState } from "./resource-schedule-types";
import {
   applyHideWeekendsToScheduler,
   applyRowHeightToScheduler,
   getAssignmentBarColorHex,
   getTimeOffLabel,
} from "./resource-schedule-helpers";
import "./resource-schedule.css";

/**
 * Generates tooltip HTML based on the hovered element type
 * @param activeTarget - The DOM element being hovered
 * @param event - The mouse event from the tooltip
 * @param scheduler - The scheduler instance
 * @param paidShiftHours - Paid shift hours for event tooltips
 * @param I18n - Internationalization context
 * @param hasPermissionToViewPeopleTimeoff - Permission to view time-off tooltips
 * @returns HTML string for the tooltip or empty string if no tooltip should be shown
 */
export function generateSchedulerTooltipHtml(
   activeTarget: HTMLElement,
   event: any,
   scheduler: SchedulerPro,
   paidShiftHours: number,
   I18n: ReturnType<typeof useI18nContext>,
   hasPermissionToViewPeopleTimeoff: boolean,
): string {
   // Determine the element type for switch-case
   let elementType: TooltipElementType = TooltipElementType.UNKNOWN;

   if (activeTarget.classList.contains(SchedulerElementClass.EVENT)) {
      elementType = TooltipElementType.EVENT;
   } else if (activeTarget.classList.contains(SchedulerElementClass.RESOURCE_NONWORKING)) {
      elementType = TooltipElementType.RESOURCE_NONWORKING;
   } else if (activeTarget.classList.contains(SchedulerElementClass.EVENT_STRIPE)) {
      elementType = TooltipElementType.EVENT_STRIPE;
   }

   let rangeRecord = null;

   switch (elementType) {
      // 1. Event Tooltips
      case TooltipElementType.EVENT: {
         const eventRecord = scheduler.resolveEventRecord(activeTarget);
         if (!eventRecord) {
            return "";
         }

         const tooltipProps: EventTooltipProps = prepareEventTooltipProps(
            eventRecord,
            paidShiftHours,
         );
         return renderToString(<GanttTooltipComponent {...tooltipProps} I18n={I18n} />);
      }
      // 2. Background Blocks (ResourceNonWorkingTime)
      case TooltipElementType.RESOURCE_NONWORKING:
         if (!hasPermissionToViewPeopleTimeoff) {
            return "";
         }
         rangeRecord =
            scheduler.features.resourceNonWorkingTime?.resolveResourceNonWorkingTimeInterval(
               activeTarget,
            );
         break;
      // 3. Event Stripes (Event Non Working Time)
      case TooltipElementType.EVENT_STRIPE: {
         if (!hasPermissionToViewPeopleTimeoff) {
            return "";
         }

         const eventRecord: any = scheduler.resolveEventRecord(activeTarget);
         if (!eventRecord) {
            return "";
         }

         // Get the resource assigned to this event
         const assignment = eventRecord.assignments?.[0];
         const resource: any = assignment?.resource;

         if (!resource) {
            return "";
         }

         const rect = scheduler.timeAxisSubGrid.element.getBoundingClientRect();
         const pageX = event.pageX;
         const x = pageX - rect.left + scheduler.timeAxisSubGrid.scrollable.x;
         const hoveredDate = scheduler.getDateFromCoordinate(x);
         const calendarId = resource.calendar?.id;
         const calendar: any = scheduler.project.calendarManagerStore.getById(calendarId);

         if (calendar && calendar.intervals) {
            for (const interval of calendar.intervals) {
               const startDate = new Date(interval.startDate);
               const endDate = new Date(interval.endDate);

               if (hoveredDate >= startDate && hoveredDate <= endDate) {
                  // Get the actual record from the store
                  rangeRecord = calendar.intervals.getById
                     ? calendar.intervals.getById(interval.id)
                     : interval;

                  // Add resourceId if not present (needed by extractTimeOffTooltipData)
                  if (rangeRecord && !rangeRecord.resourceId) {
                     rangeRecord.resourceId = resource.id;
                  }

                  break;
               }
            }
         }
         break;
      }

      default:
         return "";
   }

   if (!rangeRecord) {
      return "";
   }

   const tooltipData = extractTimeOffTooltipData(rangeRecord, scheduler, I18n);
   const tooltipHtml = renderTimeOffTooltip({
      ...tooltipData,
      I18n,
   });
   return tooltipHtml;
}

export function ResourceRenderer({
   record,
   personDetailsTearsheetRef,
   canViewPeople,
}: {
   record: SchedulerResource;
   personDetailsTearsheetRef: RefObject<GanttTearsheetRef>;
   canViewPeople: boolean;
}) {
   const isEquipment = record.resourceType === ResourceType.EQUIPMENT;

   const handlePersonClick = () => {
      personDetailsTearsheetRef.current?.handleOpenTearsheet({
         projectId: "",
         personId: record.resourceId,
         callback: () => {},
      });
   };

   return (
      <Flex alignItems="center" gap="sm">
         {isEquipment ? (
            <Excavator size="sm" color="#4f5964" className="resource-renderer-icon" />
         ) : (
            record.jobTitleColor && (
               <div
                  className="resource-renderer-job-title-dot"
                  style={{ backgroundColor: record.jobTitleColor }}
               />
            )
         )}
         <Flex direction="column" className="resource-renderer-name">
            {!isEquipment && canViewPeople ? (
               <Link onClick={handlePersonClick}>
                  <Typography weight="semibold" intent="small">
                     {record.name}
                  </Typography>
               </Link>
            ) : (
               <Typography weight="semibold" intent="small">
                  {record.name}
               </Typography>
            )}
         </Flex>
      </Flex>
   );
}

// This headerRenderer function expects us to return a string, so we're using the renderToString method to generate
// static HTML string of our React components. This static HTML does not include any JavaScript or interactivity,
// such as event handlers like onClick, so we're using a setTimeout to defer the execution of the querySelectors
// until after the current callstack is clear (ie. until after the header finishes rendering). This async deferment
// ensures that the DOM elements are ready and available when we attempt to get them with querySelector.
export const nameHeaderRenderer = ({ headerElement, column }: HeaderRenderData) => {
   const savedLayout = localStorage.getItem(SCHEDULER_EVENT_LAYOUT_KEY) || EventLayout.NONE;
   const isStackLayout = savedLayout === EventLayout.STACK;
   const updateLayout = (
      layout: EventLayout,
      iconOneEl: HTMLElement,
      iconTwoEl: HTMLElement,
   ): void => {
      const isStack = layout === EventLayout.STACK;
      iconOneEl.style.display = isStack ? "none" : "block";
      iconTwoEl.style.display = isStack ? "block" : "none";
      const scheduler = column.grid;
      scheduler.eventLayout = layout;
      localStorage.setItem(SCHEDULER_EVENT_LAYOUT_KEY, layout);
   };
   setTimeout(() => {
      const iconOne = headerElement.querySelector("#scheduler-icon-one") as HTMLElement;
      const iconTwo = headerElement.querySelector("#scheduler-icon-two") as HTMLElement;

      if (!iconOne || !iconTwo) return;

      updateLayout(savedLayout as EventLayout, iconOne, iconTwo);

      iconOne.addEventListener("click", (event) => {
         event.stopPropagation();
         event.preventDefault();
         updateLayout(EventLayout.STACK, iconOne, iconTwo);
      });
      iconTwo.addEventListener("click", (event) => {
         event.stopPropagation();
         event.preventDefault();
         updateLayout(EventLayout.NONE, iconOne, iconTwo);
      });
   }, 10);

   return `
      ${renderToString(
         <CaretsOutVertical
            id="scheduler-icon-one"
            style={{ display: isStackLayout ? "none" : "block" }}
         />,
      )}
      ${renderToString(
         <CaretsInVertical
            id="scheduler-icon-two"
            style={{ display: isStackLayout ? "block" : "none" }}
         />,
      )}
      <span>${column.text}</span>
   `;
};

export const ResourceSchedule: FC<ResourceScheduleProps> = ({
   paidShiftHours,
   personDetailsTearsheetRef,
   editAssignmentTearsheetRef,
   createAssignmentTearsheetRef,
   canViewPeople,
   loadParams,
   availablePresets,
   viewPreset,
   schedulerRef,
   hasPermissionToViewPeopleTimeoff,
   initialDateRange,
}) => {
   const I18n = useI18nContext();
   const { checkAuthAction } = usePermissionContext();
   const canManageAssignments = checkAuthAction(AuthAction.MANAGE_ASSIGNMENTS);
   const isInitialMount = useRef(true);
   const dragCreateContextRef = useRef<DragCreateState | null>(null);
   const translateTimeOffReason = (reason: string) => getTimeOffReasonDisplay(reason, I18n);
   const updateTimeOffReasonLabels = (showTimeoff: boolean) => {
      const scheduler = schedulerRef.current?.instance as any;
      const calendarStore = scheduler?.project?.calendarManagerStore;
      if (!calendarStore) return;
      scheduler.element?.classList.toggle("hide-time-off-reasons", !showTimeoff);

      calendarStore.forEach((calendar: any) => {
         const intervalsData =
            Array.isArray(calendar.intervals?.allRecords) &&
            calendar.intervals.allRecords.length > 0
               ? calendar.intervals.allRecords.map((interval: any) => interval.data ?? interval)
               : Array.isArray(calendar.intervals)
               ? calendar.intervals
               : [];

         if (!intervalsData.length) return;

         calendar.intervals = intervalsData.map((interval: any) => ({
            ...interval,
            name: showTimeoff ? getTimeOffLabel(interval, translateTimeOffReason) : "",
         }));
      });

      scheduler.refreshRows?.();
   };
   const hideWeekends = (value: boolean) => {
      const scheduler = schedulerRef.current?.instance as SchedulerPro | undefined;
      if (!scheduler) return;
      applyHideWeekendsToScheduler(scheduler, value, true);
   };

   const strips = useMemo(() => {
      const values = getGanttConfigurePanelValues();
      const items = buildCommonConfigurePanelItems({
         t: (key) => I18n.t(key),
         values,
         includeTimeOffToggle: hasPermissionToViewPeopleTimeoff,
         onProjectInformationChange: (value) => {
            updateGanttConfigurePanelLocalStorage("showProjectInformation", value);
            schedulerRef.current?.instance?.refreshRows();
         },
         onAllocationInformationChange: (value) => {
            updateGanttConfigurePanelLocalStorage("showAllocationInformation", value);
            schedulerRef.current?.instance?.refreshRows();
         },
         onTimeOffInformationChange: (value) => {
            updateGanttConfigurePanelLocalStorage("showTimeoff", value);
            updateTimeOffReasonLabels(value);
         },
         onHideWeekendsChange: (value) => {
            updateGanttConfigurePanelLocalStorage("hideWeekends", value);
            hideWeekends(value);
         },
         onRowHeightInput: (value) => {
            const scheduler = schedulerRef.current?.instance as SchedulerPro | undefined;
            if (!scheduler) return;
            applyRowHeightToScheduler(scheduler, value);
         },
         onRowHeightChange: (value) => {
            updateGanttConfigurePanelLocalStorage("rowHeight", value);
            const scheduler = schedulerRef.current?.instance as SchedulerPro | undefined;
            if (!scheduler) return;
            applyRowHeightToScheduler(scheduler, value);
         },
         onTaskBorderRadiusInput: (value) => {
            if (schedulerRef.current?.instance?.element) {
               schedulerRef.current.instance.element.style.setProperty(
                  "--scheduler-border-radius",
                  `${value}px`,
               );
            }
         },
         onTaskBorderRadiusChange: (value) => {
            updateGanttConfigurePanelLocalStorage("taskBorderRadius", value);
         },
         onAssignmentBarColorsChange: (value) => {
            updateGanttConfigurePanelLocalStorage("assignmentBarColors", value);
            schedulerRef.current?.instance?.refreshRows();
         },
      });

      return buildConfigurePanelStrip(items);
   }, [I18n, hasPermissionToViewPeopleTimeoff]);
   const schedulerConfig = useMemo(
      () => ({
         visibleDate: new Date(),
         viewPreset,
         presets: availablePresets,
         infiniteScroll: true,
         bufferCoef: 5,
         bufferThreshold: 0.2,
         ...initialDateRange,
         scheduleTooltipFeature: false,
         multiEventSelect: true,
         // Enable day-level drag/resize adjustments even when zoomed out (e.g., 8-week view)
         // Previously fillTicks: true and snap: true restricted adjustments to week-level when zoomed out
         fillTicks: false,
         snap: false,
         createEventOnDblClick: false,
         onScheduleDblClick: () => false,
         eventDragCreateFeature: getEventDragCreateFeatureConfig(canManageAssignments),
         eventDragFeature: getDragResizeFeatureConfig(DragResizeRecordKey.EVENT_RECORD, I18n),
         eventResizeFeature: getDragResizeFeatureConfig(DragResizeRecordKey.RECORD, I18n),
         onBeforeEventEdit: () => false,
         onBeforeDragCreateFinalize: getBeforeDragCreateFinalizeHandler({
            dragCreateContextRef,
            createAssignmentTearsheetRef,
            schedulerRef,
            handleDragCreate: handleDragCreateAssignment,
         }),
         dependenciesFeature: false,
         cellMenuFeature: false,
         scheduleMenuFeature: false,
         eventMenuFeature: {
            items: {
               // Disable default Bryntum menu items declaratively
               deleteEvent: false,
               unassignEvent: false,
               eventColor: false,
               editEvent: false,
               copyEvent: false,
               cutEvent: false,
               pasteEvent: false,
               splitEvent: false,
               splitTask: false,
               renameSegment: false,
               // Define custom menu items (handlers set in onEventMenuBeforeShow)
               editAssignment: {
                  weight: 0,
                  text: I18n.t("views.company.workforce_planning.assignment.edit_title"),
               },
               unassign: {
                  weight: 10,
                  text: I18n.t("views.company.workforce_planning.assignment.unassign"),
               },
               deleteAssignment: {
                  weight: 20,
                  separator: true,
                  text: I18n.t("views.company.workforce_planning.assignment.modal.delete.header"),
               },
            },
         },
         project: {
            skipNonWorkingTimeWhenSchedulingManually: false,
            eventStore: {
               fields: [{ name: "manuallyScheduled", defaultValue: true }],
            },
            phantomIdField: "phantomId",
            phantomParentIdField: "phantomParentId",
            useRawData: false,
            autoMergeAdjacentSegments: true,
            remoteFilter: true,
            lazyLoad: {
               chunkSize: 50,
               bufferUnit: "month",
               bufferAmount: 6,
            },
            autoLoad: true,
            autoSync: true,
            // /resources/sync only echoes assignment phantom ids; calendars are merged in
            // beforeLoadApply, so sync responses omit calendars.rows and Bryntum would warn.
            validateResponse: false,
            transport: {
               load: {
                  url: loadUrl,
                  method: HttpMethod.GET,
                  // Bryntum v7 dropped credentials: "include" as a default.
                  // Production is cross-origin, so we must include it to send auth cookies.
                  credentials: "include",
                  params: {
                     ...loadParams,
                     filters: loadParams?.filters ? JSON.stringify(loadParams.filters) : undefined,
                  },
               },
               sync: {
                  url: syncUrl,
                  method: HttpMethod.POST,
                  credentials: "include",
               },
            },
            // TODO(WFP-6932): Once sync supports equipment, remove isEquipmentEvent callback and equipment filtering.
            onBeforeSend: createTransformPayloadBeforeSend((eventId: string) => {
               const scheduler = schedulerRef.current?.instance;
               if (!scheduler?.project?.eventStore) return false;
               const event = scheduler.project.eventStore.getById(eventId);
               return event?.getData?.("resourceType") === ResourceType.EQUIPMENT;
            }),
            listeners: {
               beforeLoadApply({ response }: { response: any }) {
                  const scheduler = schedulerRef.current?.instance;
                  const showTimeoff = getGanttConfigurePanelValues().showTimeoff;

                  if (response.events?.rows) {
                     normalizeDateTimeFields(response.events.rows);
                  }

                  if (response.calendars?.rows && scheduler) {
                     const calendarStore = scheduler.project.calendarManagerStore;

                     response.calendars.rows.forEach((newCal: SchedulerCalendar) => {
                        if (newCal.intervals) {
                           normalizeDateTimeFields(newCal.intervals);
                           (newCal as any).intervals = newCal.intervals.map((interval: any) => ({
                              ...interval,
                              name: showTimeoff
                                 ? getTimeOffLabel(interval, translateTimeOffReason)
                                 : "",
                           }));
                        }

                        const existingCalendar: any = calendarStore.getById(newCal.id);

                        if (!existingCalendar) {
                           calendarStore.add(newCal);
                        } else {
                           const existingIntervals = existingCalendar.intervals || [];
                           const newIntervals = newCal.intervals || [];
                           const combinedIntervals = [...existingIntervals, ...newIntervals];
                           const uniqueCombinedIntervals = combinedIntervals.filter(
                              (item, index, self) =>
                                 index === self.findIndex((t) => t.id === item.id),
                           );

                           if (uniqueCombinedIntervals.length > 0) {
                              existingCalendar.intervals = [...uniqueCombinedIntervals];
                           }
                        }
                     });

                     // CRITICAL: Delete to prevent replacement
                     delete response.calendars;
                  }
               },
            },
         },
         columns: [
            {
               text: I18n.t("views.company.workforce_planning.resource"),
               field: "name",
               width: 200,
               editor: false,
               headerRenderer: nameHeaderRenderer,
               renderer: ({ record }: { record: SchedulerResource }) => (
                  <ResourceRenderer
                     record={record}
                     personDetailsTearsheetRef={personDetailsTearsheetRef}
                     canViewPeople={canViewPeople}
                  />
               ),
            },
            {
               text: I18n.t("views.company.workforce_planning.job_title"),
               field: "jobTitle",
               width: 200,
               editor: false,
               renderer: ({ record }: { record: SchedulerResource }) => (
                  <Typography intent="small">{record.jobTitle}</Typography>
               ),
            },
         ],
         rowHeight: getGanttConfigurePanelValues().rowHeight,
         barMargin: 1,
         eventLayout:
            (localStorage.getItem(SCHEDULER_EVENT_LAYOUT_KEY) as EventLayout) || EventLayout.NONE,
         height: "calc(100vh - 250px)",
         fillLastColumn: true,
         readOnly: false,
         responsive: true,
         eventRenderer: ({ eventRecord, renderData }: any) => {
            if (!eventRecord.getData("projectId")) {
               return I18n.t("views.company.workforce_planning.assignment.new_event");
            }

            const { assignmentBarColors, showAllocationInformation, showProjectInformation } =
               getGanttConfigurePanelValues();
            const barColor = getAssignmentBarColorHex(
               eventRecord,
               assignmentBarColors as AssignmentBarColors,
               paidShiftHours,
            );
            if (barColor) {
               const [, , lightestColor] = getColorShades(barColor);
               renderData.style = `
               border-width: 2px;
               border-style: solid;
               border-color: ${barColor};
               background-color: ${lightestColor};
               color: ${getTextColorForBackground(lightestColor)};
               padding: 2px 6px;
            `;
            }

            const renderedParts: string[] = [];

            if (showProjectInformation && eventRecord.projectName) {
               renderedParts.push(eventRecord.projectName);
            }

            if (showAllocationInformation) {
               const { isTaskWithTime, taskTimeAllocation } =
                  prepareEventTooltipProps(eventRecord, paidShiftHours) || {};

               if (taskTimeAllocation != null && taskTimeAllocation !== "") {
                  renderedParts.push(`${taskTimeAllocation}${isTaskWithTime ? "" : "%"}`);
               }
            }

            return renderedParts.join(" | ");
         },
         onBeforeEventResizeFinalize: ({
            eventRecord,
            startDate,
            endDate,
            finalize,
         }: {
            eventRecord: EventModel;
            startDate: Date;
            endDate: Date;
            finalize: (result: boolean) => void;
         }) => {
            // TODO(WFP-6932): Remove equipment guard once sync supports equipment assignments.
            if (eventRecord.getData("resourceType") === ResourceType.EQUIPMENT) {
               finalize(false);
               return;
            }
            finalize(
               validateEventPlacement(
                  startDate,
                  endDate,
                  eventRecord.getData("workDays") as Record<string, boolean> | null,
               ),
            );
         },
         onBeforeEventDropFinalize: ({
            context,
         }: {
            context: {
               eventRecords: EventModel[];
               startDate: Date;
               endDate: Date;
               newResource: ResourceModel;
               valid: boolean;
            };
         }) => {
            const eventRecord = context.eventRecords[0];
            // TODO(WFP-6932): Remove equipment guard once sync supports equipment assignments.
            if (eventRecord?.getData("resourceType") === ResourceType.EQUIPMENT) {
               context.valid = false;
               return;
            }
            context.valid = validateEventPlacement(
               context.startDate,
               context.endDate,
               eventRecord.getData("workDays") as Record<string, boolean> | null,
            );
         },
         onEventResizeEnd: ({ eventRecord }: { eventRecord: EventModel }) =>
            adjustEventEndDate(eventRecord),
         onEventSegmentResizeEnd: ({ eventRecord }: { eventRecord: EventModel }) =>
            adjustEventEndDate(eventRecord),
         eventTooltipFeature: false, // Disabled to use custom tooltip
         onEventMenuBeforeShow: ({
            items,
            eventRecord,
            assignmentRecord,
         }: {
            items: Record<string, any>;
            eventRecord: EventModel;
            assignmentRecord: any;
         }) => {
            // TODO(WFP-6931): Remove equipment guard once context menu actions (edit, unassign, delete) are supported for equipment.
            if (
               eventRecord.getData?.("resourceType") === ResourceType.EQUIPMENT ||
               !canManageAssignments
            ) {
               return false;
            }

            const assignmentId =
               eventRecord.getData?.("assignmentId") ?? eventRecord.id ?? assignmentRecord?.id;
            const projectId = eventRecord.getData?.("projectId") ?? (eventRecord as any).projectId;

            items.editAssignment.onItem = () =>
               handleEditAssignment({
                  assignmentId: String(assignmentId),
                  projectId: String(projectId),
                  editAssignmentTearsheetRef,
                  schedulerRef,
               });

            items.unassign.onItem = () =>
               handleUnassignAssignment({
                  eventRecord,
                  schedulerRef,
               });

            items.deleteAssignment.onItem = () =>
               handleDeleteAssignment({
                  eventRecord,
                  schedulerRef,
                  title: I18n.t("views.company.workforce_planning.assignment.modal.delete.header"),
                  body: (
                     <P>
                        {I18n.t(
                           "views.company.workforce_planning.assignment.modal.delete.description",
                        )}
                     </P>
                  ),
               });
         },
         timeRangesFeature: getTimeRangesFeature(),
         resourceNonWorkingTimeFeature: {
            maxTimeAxisUnit: "year",
            enableMouseEvents: true,
         },
         eventNonWorkingTimeFeature: {
            mode: "both",
         },
      }),
      [
         I18n,
         paidShiftHours,
         personDetailsTearsheetRef,
         editAssignmentTearsheetRef,
         createAssignmentTearsheetRef,
         canViewPeople,
         canManageAssignments,
      ],
   );

   // Custom Tooltip for Event Non Working Time, Resource Non Working Time and Event Tooltip
   useEffect(() => {
      const scheduler = schedulerRef.current?.instance;
      if (!scheduler) return;
      const { hideWeekends, rowHeight, showTimeoff } = getGanttConfigurePanelValues();

      applyRowHeightToScheduler(scheduler, rowHeight);
      applyHideWeekendsToScheduler(scheduler, hideWeekends, false);
      scheduler.element?.classList.toggle("hide-time-off-reasons", !showTimeoff);

      const customTooltip = new Tooltip({
         rootElement: document.body,
         forSelector: ".b-sch-resource-non-working-time, .b-sch-non-working-time, .b-sch-event",
         hoverDelay: 200,
         listeners: {
            beforeShow({ source }: { source: any }) {
               const isEvent = source.activeTarget?.classList.contains(SchedulerElementClass.EVENT);
               if (isEvent) {
                  source.maxWidth = "unset";
                  source.minWidth = "400px";
                  source.width = "max-content";
                  source.style = "border-radius: 6px;";
               } else {
                  source.maxWidth = null;
                  source.minWidth = null;
                  source.width = null;
                  source.style = "";
               }
            },
         },
         // v7: getHtml now expects async/Promise return type
         getHtml: async ({ activeTarget, event }) => {
            return generateSchedulerTooltipHtml(
               activeTarget,
               event,
               scheduler,
               paidShiftHours,
               I18n,
               hasPermissionToViewPeopleTimeoff,
            );
         },
      });

      return () => customTooltip?.destroy();
   }, [schedulerRef, I18n, paidShiftHours, hasPermissionToViewPeopleTimeoff]);

   useEffect(() => {
      if (isInitialMount.current) {
         isInitialMount.current = false;
         return;
      }
      const scheduler = schedulerRef.current?.instance;
      if (!scheduler?.project) {
         return;
      }

      const run = async () => {
         const project = scheduler.project;

         const newParams = {
            ...loadParams,
            filters: loadParams?.filters ? JSON.stringify(loadParams.filters) : undefined,
         };

         (project as any).transport.load.params = newParams;

         if (project.resourceStore) project.resourceStore.data = [];
         if (project.calendarManagerStore) project.calendarManagerStore.data = [];

         await project.load();
      };

      run();
   }, [loadParams]);

   return (
      <BryntumSchedulerPro
         width="100%"
         ref={schedulerRef}
         {...(schedulerConfig as any)}
         strips={strips}
         onBeforePresetChange={(preset: any) => {
            if (preset.to === undefined) return;
            localStorage.setItem("gantt-view-preset", String(preset.to.id));
         }}
      />
   );
};

export default ResourceSchedule;
